import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { getEvent, updateEventStatus, updateEventById } from 'api/canvms';
import { displayErrorMessage, displaySuccessMessage } from 'redux/notificationBar/notificationBarSlice';
import type { VMSEvent } from '../types/canvision.types';

interface UseEventDetailOptions {
  eventId: number | null;
  siteId: number | null;
  enabled?: boolean;
}

export function useEventDetail({ eventId, siteId, enabled = true }: UseEventDetailOptions) {
  const dispatch = useDispatch();
  const [event, setEvent] = useState<VMSEvent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialLoadDone = useRef(false);

  const fetchEvent = useCallback(async (isRefresh = false) => {
    if (!eventId) {
      setEvent(null);
      return;
    }

    try {
      if (!isRefresh && !initialLoadDone.current) {
        setLoading(true);
      }
      setError(null);
      const response = await getEvent(eventId);
      setEvent(response.data);
      initialLoadDone.current = true;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to fetch event';
      console.error('[CANVMS/fetchEventDetail] Error fetching event:', err);
      setError(message);
      dispatch(displayErrorMessage(message));
    } finally {
      setLoading(false);
    }
  }, [eventId, dispatch]);

  useEffect(() => {
    if (enabled && eventId) {
      initialLoadDone.current = false;
      fetchEvent(false);
    } else {
      setEvent(null);
      initialLoadDone.current = false;
    }
  }, [eventId, enabled, fetchEvent]);

  const updateStatus = useCallback(async (status: 'new' | 'acknowledged' | 'dismissed' | 'promoted') => {
    if (!eventId) return;

    try {
      if (siteId) {
        await updateEventStatus(siteId, eventId, status);
      } else {
        await updateEventById(eventId, status);
      }
      await fetchEvent(true);
      dispatch(displaySuccessMessage(`Event status updated to ${status}`));
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to update event status';
      console.error('[CANVMS/updateEventDetailStatus] Error updating event status:', err);
      dispatch(displayErrorMessage(message));
      throw err;
    }
  }, [eventId, siteId, fetchEvent, dispatch]);

  const refetch = useCallback(() => fetchEvent(true), [fetchEvent]);

  return {
    event,
    loading,
    error,
    updateStatus,
    refetch,
  };
}
